import { z } from 'zod'

import type { Guide } from '@/project/version/guide'
import type { Reference } from '@/project/version/reference'
import { nanoid, nanoidSchema } from '@/utility'

const headerLinkBaseSchema = z
  .object({
    uid: nanoidSchema,
    title: z.string(),
    show: z.boolean().default(true),
    isStarred: z.boolean().default(false),
    type: z
      .union([
        z.literal('guide'),
        z.literal('reference'),
        z.literal('link'),
        z.literal('group'),
        z.literal('version-selector'),
        z.literal('spacer'),
      ])
      .default('link'),
  })
  .meta({ id: 'header-link-base' })

export type HeaderLinkReference = z.infer<typeof headerLinkReferenceSchema>

const headerLinkReferenceSchema = headerLinkBaseSchema
  .extend({
    type: z.literal('reference'),
    /** Whether the reference has a permalinked reference or not */
    hasPermalink: z
      .boolean()
      .default(false)
      .describe('Whether the reference has a permalinked reference or not'),
    hasManagedDoc: z
      .boolean()
      .default(false)
      .describe('Whether the reference has a linked registry API'),
  })
  .meta({ id: 'header-link-reference' })

export type HeaderLinkGuide = z.infer<typeof headerLinkGuideSchema>

const headerLinkGuideSchema = headerLinkBaseSchema
  .extend({
    type: z.literal('guide'),
  })
  .meta({ id: 'header-link-guide' })

/** Header links for guides and references where we navigate by route id */
export type HeaderLinkInternal = HeaderLinkGuide | HeaderLinkReference

export type HeaderLinkVersionSelector = z.infer<
  typeof headerLinkVersionSelectorSchema
>

const headerLinkVersionSelectorSchema = headerLinkBaseSchema
  .extend({
    type: z.literal('version-selector'),
  })
  .meta({ id: 'header-link-version-selector' })

export type HeaderLinkSpacer = z.infer<typeof headerLinkSpacerSchema>

const headerLinkSpacerSchema = headerLinkBaseSchema
  .extend({
    type: z.literal('spacer'),
  })
  .meta({ id: 'header-link-spacer' })

/** External links that directly contain the href url */
export type HeaderLinkExternal = z.infer<typeof headerLinkExternalSchema>

const headerLinkExternalSchema = headerLinkBaseSchema
  .extend({
    type: z.literal('link'),
    isButton: z.boolean().default(false),
    link: z.string(),
  })
  .meta({ id: 'header-link-external' })

/** Header links for groups where we render children links */
export type HeaderLinkGroup = z.output<typeof headerLinkGroupSchema>

const headerLinkGroupSchema = headerLinkBaseSchema
  .extend({
    type: z.literal('group'),
    /** Children of the group. Groups cannot contain other groups, spacers, or the version selector */
    children: z
      .array(
        z.union([
          headerLinkGuideSchema,
          headerLinkReferenceSchema,
          headerLinkExternalSchema,
        ]),
      )
      .describe(
        'Children of the group. Groups cannot contain other groups, spacers, or the version selector',
      ),
  })
  .meta({ id: 'header-link-group' })

/**
 * Header links are polymorphic and can be used for guides, references, links, and groups
 *
 * Check the `type` field to determine how to link appropriately.
 *
 * Guides and references will include the appropriate UID for the route in the `link` field
 * Links will include the `link` field as the href
 * Groups will include the `children` field as the list of uids to render as children
 */
export type HeaderLink = z.output<typeof headerLinkSchema>

export const headerLinkSchema = z
  .union([
    headerLinkGuideSchema,
    headerLinkReferenceSchema,
    headerLinkGroupSchema,
    headerLinkExternalSchema,
    headerLinkVersionSelectorSchema,
    headerLinkSpacerSchema,
  ])
  .meta({ id: 'header-link' })

/**
 * Create the complete list of header links for the guides and references
 * This will be run as part of the version schema egress to ensure that
 * guide and reference properties are always propagated to the corresponding header link
 *
 * This eliminates the need to update the field on the header link itself
 */
export function getHeaderLinks({
  guides,
  references,
  header,
}: {
  header: HeaderLink[]
  guides: Record<string, Guide>
  references: Record<string, Reference>
}): HeaderLink[] {
  // The version handles the determined order of the header links
  // We filter out any links that are not present in the guides or references
  // During publish we may remove guides or references without content
  const complete: HeaderLink[] = [...header]
    .filter((h) => {
      if (h.type === 'guide') return !!guides[h.uid]
      if (h.type === 'reference') return !!references[h.uid]

      return true
    })
    // Check header group children for matching guides/references
    .map((header) => {
      if (header.type !== 'group') return header

      return {
        ...header,
        // Only include header children if the guide/reference is available
        children: header.children.filter((c) => {
          if (c.type === 'guide') return !!guides[c.uid]
          if (c.type === 'reference') return !!references[c.uid]

          return true
        }),
      }
    })

  const orderedIds: string[] = []
  complete.forEach((h) => {
    orderedIds.push(h.uid)
    if (h.type === 'group') {
      orderedIds.push(...h.children.map((c) => c.uid))
    }
  })

  // Add any missing links from the guides
  Object.values(guides).forEach((g) => {
    if (!orderedIds.includes(g.uid)) {
      complete.push({
        uid: g.uid,
        title: g.title,
        show: g.show,
        isStarred: g.isStarred,
        type: 'guide',
      } satisfies HeaderLinkGuide)
    }
  })

  // Add any missing links from the references
  Object.values(references).forEach((r) => {
    if (!orderedIds.includes(r.uid)) {
      complete.push({
        uid: r.uid,
        title: r.title,
        show: r.show,
        isStarred: r.isStarred,
        type: 'reference',
        hasPermalink: !!r.specPermalink,
        hasManagedDoc: !!r.managedDoc,
      } satisfies HeaderLinkReference)
    }
  })

  // Add the version selector if it is not already present
  if (!complete.find((e) => e.type === 'version-selector')) {
    complete.push({
      uid: nanoid(),
      title: 'Version Selector',
      type: 'version-selector',
      show: true,
      isStarred: false,
    } satisfies HeaderLinkVersionSelector)
  }

  // We will store the primary properties in the guide and reference
  // and then use them as the source of truth for the header links
  complete.forEach((element) => {
    const guide = guides[element.uid]
    const reference = references[element.uid]

    if (element.type === 'guide' && guide) {
      element.title = guide.title
      element.show = guide.show
      element.isStarred = guide.isStarred
    }
    if (element.type === 'reference' && reference) {
      element.title = reference.title
      element.show = reference.show
      element.isStarred = reference.isStarred
      element.hasPermalink = !!reference.specPermalink
      element.hasManagedDoc = !!reference.managedDoc
    }

    if (element.type === 'group') {
      element.children.forEach((c) => {
        const childGuide = guides[c.uid]
        const childReference = references[c.uid]

        if (c.type === 'guide' && childGuide) {
          c.title = childGuide.title
          c.show = childGuide.show
          c.isStarred = childGuide.isStarred
        }
        if (c.type === 'reference' && childReference) {
          c.title = childReference.title
          c.show = childReference.show
          c.isStarred = childReference.isStarred
          c.hasPermalink = !!childReference.specPermalink
          c.hasManagedDoc = !!childReference.managedDoc
        }
      })
    }
  })
  // Add any missing links from the guides
  return complete
}
